import {
  Component,
  OnInit,
  ViewChild,
  TemplateRef,
  ElementRef,
  AfterViewInit,
  inject,
} from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CommonService } from 'src/app/services/common.service';
import { StoreService } from 'src/app/services/store.service';
import { ResponsiveLayoutService } from 'src/app/services/responsive-layout.service';
import { Plant } from 'src/app/models/plant';
import { StepService } from 'src/app/services/step.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { REGIONES_COMUNA } from 'src/app/shared/utils/regiones';
import { Order } from 'src/app/models/order';
import { ModalAlertComponent } from 'src/app/shared/modal-alert/modal-alert.component';
import { RutService } from 'src/app/services/rut.service';
import { SwalAlertService } from 'src/app/services/swal-alert.service';
import { AuthenticationService } from 'src/app/services/authentication.service';

@Component({
  selector: 'app-mapa',
  templateUrl: './mapa.component.html',
  styleUrls: ['./mapa.component.css'],
})
export class MapaComponent implements OnInit, AfterViewInit {
  @ViewChild('content', { static: true }) modalContent!: TemplateRef<any>;
  @ViewChild('selectMap', { static: true }) selectMap!: ElementRef;
  @ViewChild('contentUser', { static: true }) contentUser!: ElementRef;
  swal = inject(SwalAlertService);

  lat: number = -33.452;
  lng: number = -70.643007;
  center: google.maps.LatLngLiteral = { lat: -33.452, lng: -70.643007 };
  zoom = 15;
  geocoder = new google.maps.Geocoder();
  form: FormGroup;
  plants!: Observable<Plant[]>;
  plants_comunas!: Observable<any[]>;
  dispatch!: Observable<Order>;
  formUser: FormGroup;
  regionesComunas = REGIONES_COMUNA;

  complementaryAddress: string = '';
  complementaryAddressData = {
    region: '',
    comuna: '',
    street: '',
    number: '',
    address: '',
    lat: 0,
    lng: 0,
  };

  mapOptions = {
    scrollwheel: true, // Habilita el zoom con la rueda del ratón
    gestureHandling: 'auto', // Permite la interacción estándar con el mapa
  };

  blueMarkerVisible: boolean = false;
  optional: boolean = false;

  /** Radio en metros para considerar "planta cercana" por distancia; si la más cercana supera esto, se usa fallback comuna-planta. */
  private static readonly DISTANCIA_MAX_METROS_PLANTA_CERCANA = 40000;
  /** BD: planta.distancia_max esta en KILOMETROS. Ej: 40 en BD = 40 km; en codigo se usa como 40000 m. */

  constructor(
    private readonly commonService: CommonService,
    private readonly spinner: NgxSpinnerService,
    private readonly storeService: StoreService,
    public responsiveService: ResponsiveLayoutService,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly stepService: StepService,
    private readonly modalService: NgbModal,
    private readonly rutService: RutService,
    private readonly authenticationService: AuthenticationService,
  ) {
    const formulario = this.commonService.getAddress() || {};
    this.form = this.fb.group({
      lat: [formulario?.lat ?? '', [Validators.required]],
      lng: [formulario?.lng ?? '', [Validators.required]],
      comuna: [formulario?.comuna ?? '', [Validators.required]],
      region: [formulario?.region ?? '', [Validators.required]],
      street: [formulario?.street ?? '', [Validators.required]],
      number: [formulario?.number ?? '', [Validators.required]],
      address: [formulario?.address ?? '', [Validators.required]],
    });

    this.formUser = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      rut: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    this.stepService.changeStep(1);
    this.plants = this.storeService.get_plant_actives().pipe(shareReplay(1));
    this.plants_comunas = this.storeService.get_comuna_planta_active().pipe(shareReplay(1));

    // Establece posiciones iniciales distintas
    this.lat = -33.452; // Coordenadas del marcador principal
    this.lng = -70.643007;

    this.complementaryAddressData.lat = -33.453; // Coordenadas del marcador complementario
    this.complementaryAddressData.lng = -70.644007;
  }
  ngAfterViewInit(): void {
    if (this.commonService.getFormMap()) {
      this.form = this.commonService.getFormMap()
    }
    if (this.commonService.getComplementaryAddress()) {
      const complementary = this.commonService.getComplementaryAddress();
      this.verificar(complementary)
      this.complementaryAddressData = complementary;
      this.complementaryAddress = complementary.address;
    }

    this.blueMarkerVisible = this.commonService.getblueMarkerVisible();
  }

  checkRUT() {
    const rutControl = this.formUser?.get('rut');
    const cleanValue = this.rutService.rutClean(rutControl?.value);
    const valid = this.rutService.getRutChile(0, cleanValue);

    if (valid) {
      rutControl?.setErrors(null);
      rutControl?.setValue(this.rutService.rutFormat(cleanValue));
    } else {
      rutControl?.setErrors({ noEsIgual: true });
    }
  }

  get comunaByRegion() {
    const region = this.form?.get('region')?.value;
    if (region) {
      let arreglo = REGIONES_COMUNA.find(
        (r) => r.nombreRegion == region
      )?.comunas;
      return arreglo?.sort((a, b) => a.localeCompare(b));
    }
    return [];
  }

  getCoords(address: string, updateBlueMarker: boolean = false, complementaryAddressData: any | null) {
    this.spinner.show();
    this.geocoder.geocode({ address }, (results, status) => {
      if (
        status === google.maps.GeocoderStatus.OK &&
        results &&
        results.length > 0
      ) {
        const location = results[0].geometry.location;

        // Actualizar coordenadas y formulario
        this.lat = location.lat();
        this.lng = location.lng();
        this.center = { lat: this.lat, lng: this.lng };
        // Validar y actualizar dirección
        this.checkAddress(results[0]);
        const direccion = this.optional ? results[0].formatted_address : this.getDireccionFormateada;
        this.form.patchValue({
          lat: this.lat,
          lng: this.lng,
          address: direccion,
        });
        // Lógica adicional para el marcador azul
        if (updateBlueMarker) {
          if (complementaryAddressData) {
            this.complementaryAddressData.lat = complementaryAddressData.lat;
            this.complementaryAddressData.lng = complementaryAddressData.lng;
          } else {
            this.complementaryAddressData.lat = this.lat;
            this.complementaryAddressData.lng = this.lng;
          }
          this.blueMarkerVisible = true;
          this.zoom = 18; // Recentrar y acercar el mapa
        }
      } else {
        this.form.setErrors({ noValid: true });
      }
      this.spinner.hide();
    });
  }

  checkAddress(place: google.maps.GeocoderResult) {
    const components = place.address_components;
    const street =
      components.find((x) => x.types.includes('route'))?.long_name || this.form.value.street;
    const number =
      components.find((x) => x.types.includes('street_number'))?.long_name ||
      this.form.value.number;
    this.optional = components.find((x) => x.types.includes('street_number'))?.long_name != null;
    this.form.patchValue({
      street: street,
      number: number,
      address: place.formatted_address,
    });
  }

  get verificaTodosCampos() {
    const formValue = this.form.value;
    const { street, number, comuna, region } = this.form.value;

    return [street, number, comuna, region].some(valor =>
      this.isEmptyOrNull(valor)
    );
  }

  isEmptyOrNull(valor: string) {
    if (!valor) {
      return true;
    }
    return valor.trim() == '';

  }

  verificar(complementaryAddressData: any | null) {
    this.complementaryAddress = '';
    this.complementaryAddressData = {
      region: '',
      comuna: '',
      street: '',
      number: '',
      address: '',
      lat: 0,
      lng: 0,
    };
    const address = this.getDireccionFormateada;
    this.getCoords(address, true, complementaryAddressData); // Actualiza también el marcador azul
    this.form.markAsPristine();
  }
  get getDireccionFormateada() {
    const formValue = this.form.value;
    return `${formValue.street} ${formValue.number}, ${formValue.comuna}, ${formValue.region}`;
  }
  get enabledSave() {

    return this.form.valid && this.form.pristine;
  }

  async guardar() {
    if (this.form.valid) {
      if (!this.optional && this.complementaryAddress == '') {
        this.showComplementaryRequired();
        return;
      }
      if (this.complementaryAddress !== '' && (this.form.value.comuna !== this.complementaryAddressData.comuna)) {
        await this.swal.confirmarTexto(
          'La dirección complementaria debe estar dentro de la misma comuna que la dirección de despacho. Por favor, verifique ambas direcciones.',
          'warning',
          {
            title: 'Atención',
            confirmButtonText: 'OK',
            showCancelButton: false
          }
        );
        return;
      }
      await this.validaPlantaGuardado();
    }
  }
  async validaPlantaGuardado() {
    this.spinner.show();
    const plantas = await this.getPlant();
    const plant = plantas.panta;
    let esSujetaFactibilidad = plantas.complementaria;
    const porComuna = plantas.porComuna === true;

    let distanciaPinAzulMetros: number | null = null;
    if (plant && this.complementaryAddress !== '') {
      distanciaPinAzulMetros = await this.getDistanciaMetrosHastaPlanta(
        this.complementaryAddressData.lat,
        this.complementaryAddressData.lng,
        plant
      );
      const umbralPlantaMetros = await this.getUmbralMetrosPorPlanta(plant);
      if (distanciaPinAzulMetros !== null && distanciaPinAzulMetros >= umbralPlantaMetros) {
        esSujetaFactibilidad = true;
      }
    }


    if (plant) {
      if (!porComuna) {
        const distPinRojoMetros = await this.getDistanciaMetrosHastaPlanta(
          this.form.get('lat')?.value,
          this.form.get('lng')?.value,
          plant
        );
        if (distPinRojoMetros !== null && distPinRojoMetros > MapaComponent.DISTANCIA_MAX_METROS_PLANTA_CERCANA) {
          this.spinner.hide();
          this.showAlert();
          return;
        }
      }
      this.spinner.hide();
      if (esSujetaFactibilidad) {
        const ok = await this.swal.confirmarTexto(
          'Por factibilidad técnica, pedido quedará sujeto a confirmación de despacho',
          'warning',
          {
            title: 'Aviso',
            confirmButtonText: 'OK',
            reverseButtons: true,
            focusCancel: true,
          }
        );
        if (!ok) return;
      }

      this.continuarGuardando(plant, esSujetaFactibilidad);

    } else {
      this.spinner.hide();
      this.showAlert();
    }
  }
  async continuarGuardando(plant, esComplementaria) {
    const normalLocation = {
      lat: this.form.get('lat')?.value,
      lng: this.form.get('lng')?.value,
      address: this.form.get('address')?.value,
    };
    this.commonService.setLocation(normalLocation, plant, this.complementaryAddressData, this.blueMarkerVisible, this.form, esComplementaria);



    // Se omite el modal "Datos de Contacto" para pruebas de flujo.
    // Si Flow/Webpay fallan por falta de register_user (order.id / order_buy), reactivar el bloque inferior.
    // const showContactModal = this.needUser();
    // if (showContactModal) {
    //   this.modalService.open(this.contentUser, { centered: true });
    // } else {
    //   this.router.navigateByUrl('/producto');
    // }
    sessionStorage.removeItem('productSelectionModalShown');
    this.router.navigateByUrl('/producto');
  }



  showAlert() {
    const modalRef = this.modalService.open(ModalAlertComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = 'Plantas Deshabilitada';
    modalRef.componentInstance.description =
      'Para la dirección seleccionada no se encuentra una planta cercana para su despacho.';
  }

  showComplementaryRequired() {
    const modalRef = this.modalService.open(ModalAlertComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = 'Dirección Complementaria';
    modalRef.componentInstance.description =
      'Debe ingresar la dirección complementaria.';
    this.blueMarkerVisible = true;
  }

  async getPlant(): Promise<{ panta: number | null; complementaria: boolean; porComuna?: boolean }> {
    let lat = this.form.get('lat')?.value;
    let long = this.form.get('lng')?.value;
    let plantDistFromLatLong = await this.getPlantDistFromLatLong(lat, long);
    if (plantDistFromLatLong) {
      return { ...plantDistFromLatLong, porComuna: false };
    }
    try {
      if (this.complementaryAddress !== '') {
        lat = this.complementaryAddressData.lat;
        long = this.complementaryAddressData.lng;
        plantDistFromLatLong = await this.getPlantDistFromLatLong(lat, long);
      }
      if (plantDistFromLatLong) {
        return { ...plantDistFromLatLong, porComuna: false };
      }
    } catch {
      // Fallback a comuna-planta si falla búsqueda por dirección complementaria
    }
    const pantaComuna = await this.getComunaPlant();
    return { panta: pantaComuna, complementaria: false, porComuna: true };
  }


  async getPlantDistFromLatLong(lat: number, long: number): Promise<{ panta: number; complementaria: boolean } | null> {
    try {
      const result = await this.getPlantDist(lat, long);
      if (!result) {
        return null;
      }
      if (result.distanceMetros >= MapaComponent.DISTANCIA_MAX_METROS_PLANTA_CERCANA) {
        return null;
      }
      const esSujetaFactibilidad = result.distanceMetros >= result.thresholdMetros;
      return { panta: result.plantId, complementaria: esSujetaFactibilidad };
    } catch {
      return null;
    }
  }







  async getComunaPlant() {
    let res: number | null = null;
    let comuna = this.form.get('comuna')?.value;
    let plantLocations = await firstValueFrom(this.plants_comunas).then(
      (plants) => {
        return plants?.find((x) => x.comuna.name == comuna);
      },
      () => {
        return {};
      }
    );
    if (plantLocations) {
      res = plantLocations.plant;
      this.commonService.setfineOrdenData(plantLocations.fine ?? 0);
    } 
    return res;
  }

  /** Umbral por planta = distancia_max (km) del backend; 0 = sin limite (nunca factibilidad por distancia). */
  async getPlantDist(lat: number, long: number): Promise<{ plantId: number; distanceMetros: number; thresholdMetros: number } | null> {
    const plantLocations = await firstValueFrom(this.plants).then(
      (plants: Plant[]) =>
        plants?.map((p: Plant) => {
          const distMaxKm = p.distancia_max ?? 0;
          return {
            id: p.id,
            thresholdMetros: distMaxKm > 0 ? distMaxKm * 1000 : Number.MAX_SAFE_INTEGER,
            location: new google.maps.LatLng(p.latitud, p.longitud),
          };
        }) ?? [],
      () => [] as { id: number; thresholdMetros: number; location: google.maps.LatLng }[]
    );

    let minDistanceMetros = Infinity;
    let minPlantId: number | null = null;
    let minThresholdMetros = Infinity;

    for (let index = 0; index < 5; index++) {
      const chunk = plantLocations.slice(20 * index, 20 * (index + 1));
      if (!chunk.length) break;
      const requestLocation = new google.maps.LatLng(lat, long);
      const elements: { distance?: { value: number }; status?: string }[] = await new Promise((resolve) =>
        this.getDistance(resolve, requestLocation, chunk)
      );
      if (!elements) continue;
      elements.forEach((e: { distance?: { value: number }; status?: string }, i: number) => {
        if (e.status !== 'OK' || e.distance?.value == null) return;
        const distMetros = e.distance.value;
        if (distMetros < minDistanceMetros) {
          minDistanceMetros = distMetros;
          minPlantId = chunk[i].id;
          minThresholdMetros = chunk[i].thresholdMetros;
        }
      });
    }

    this.commonService.setfineOrdenData(0);
    if (minPlantId == null) {
      return null;
    }
    return { plantId: minPlantId, distanceMetros: minDistanceMetros, thresholdMetros: minThresholdMetros };
  }
  async getPlantDistComplementaria(): Promise<number | null> {
    const lat = this.form.get('lat')?.value;
    const lng = this.form.get('lng')?.value;
    if (lat == null || lng == null) return null;
    const result = await this.getPlantDist(lat, lng);
    this.commonService.setfineOrdenData(0);
    return result?.plantId ?? null;
  }

  /** Umbral en metros para la planta. En BD distancia_max va en KILOMETROS; aqui se convierte a metros. 0 = sin limite. */
  async getUmbralMetrosPorPlanta(plantId: number): Promise<number> {
    const plants = await firstValueFrom(this.plants);
    const p = plants?.find((x: Plant) => x.id === plantId);
    const distMaxKm = p?.distancia_max ?? 0;
    const umbralMetros = (!p || distMaxKm <= 0) ? Number.MAX_SAFE_INTEGER : distMaxKm * 1000;
    return umbralMetros;
  }

  async getDistanciaMetrosHastaPlanta(lat: number, lng: number, plantId: number): Promise<number | null> {
    const plantLocations = await firstValueFrom(this.plants).then(
      (plants: Plant[]) => {
        const p = plants?.find((x: Plant) => x.id === plantId);
        if (!p) return null;
        return [{ id: p.id, location: new google.maps.LatLng(p.latitud, p.longitud) }];
      },
      () => null
    );
    if (!plantLocations) return null;
    const requestLocation = new google.maps.LatLng(lat, lng);
    const elements: { distance?: { value: number }; status?: string }[] = await new Promise((resolve) =>
      this.getDistance(resolve, requestLocation, plantLocations)
    );
    if (!elements?.length || elements[0].status !== 'OK' || elements[0].distance?.value == null) {
      return null;
    }
    const metros = elements[0].distance.value;
    return metros;
  }

  getDistance(resolve: any, requestLocation: any, plantLocations: any) {
    const service = new google.maps.DistanceMatrixService();

    return service.getDistanceMatrix(
      {
        origins: [requestLocation],
        destinations: plantLocations.map((x: any) => x.location),
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC,
        avoidHighways: false,
        avoidTolls: false,
      },
      (response, status) => {
        if (status == google.maps.DistanceMatrixStatus.OK)
          resolve(response?.rows[0].elements);
        else resolve(null);
      }
    );
  }

  //Funcion con logica de negocio para obtener si es necesario o no mostrar modal del usuario
  needUser() {
    return !this.commonService.getUser();
  }

  async saveUser() {
    let usuario = this.formUser.value;
    const adminUserId = this.authenticationService.adminUserId;
    if (typeof adminUserId === 'number') {
      usuario['user'] = adminUserId;
    }
    this.storeService.create_user_dispatch(usuario).subscribe({
      next: (e) => {
        this.commonService.setUserOrdenData(e, usuario);
        this.commonService.setUser(usuario);
        const address = this.form.value;
        this.commonService.setMap(address, usuario);
        this.modalService.dismissAll();
        this.router.navigateByUrl('/producto');
        this.spinner.hide();
      },
    });
  }
  //Funcion valida que usuario este valido
  get ngUserValid() {
    return this.formUser.valid;
  }

  onMarkerDragEnd(event: google.maps.MapMouseEvent) {
    if (event.latLng) {
      this.lat = event.latLng.lat();
      this.lng = event.latLng.lng();

      // Actualizar la dirección complementaria
      this.geocoder.geocode(
        { location: { lat: this.lat, lng: this.lng } },
        (results, status) => {
          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results.length > 0
          ) {
            const location = results[0];
            this.updateComplementaryAddress(location);
          }
        }
      );
    }
  }

  updateComplementaryAddress(location: google.maps.GeocoderResult) {
    const components = location.address_components;
    const region =
      components.find((x) => x.types.includes('administrative_area_level_1'))
        ?.long_name || '';
    const comuna =
      components.find((x) => x.types.includes('administrative_area_level_3'))
        ?.long_name || '';
    const street =
      components.find((x) => x.types.includes('route'))?.long_name || this.complementaryAddressData.street;
    const number =
      components.find((x) => x.types.includes('street_number'))?.long_name ||
      this.complementaryAddressData.number;

    this.complementaryAddressData = {
      region: region,
      comuna: comuna,
      street: street,
      number: number,
      address: location.formatted_address,
      lat: this.complementaryAddressData.lat, // Mantén las coordenadas complementarias
      lng: this.complementaryAddressData.lng, // Mantén las coordenadas complementarias
    };

    this.complementaryAddress = location.formatted_address; // Actualiza solo el texto
  }

  onComplementaryMarkerDragEnd(event: google.maps.MapMouseEvent) {
    if (event.latLng) {
      this.complementaryAddressData.lat = event.latLng.lat();
      this.complementaryAddressData.lng = event.latLng.lng();

      // Actualiza la dirección complementaria
      this.geocoder.geocode(
        {
          location: {
            lat: this.complementaryAddressData.lat,
            lng: this.complementaryAddressData.lng,
          },
        },
        (results, status) => {
          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results.length > 0
          ) {
            const location = results[0];
            this.updateComplementaryAddress(location);
          }
        }
      );
    }
  }
}
